import type { AttributeGroupListItem, ConversationAttributeItem } from '@/entities/attributes';
import type { ConversationAttrValueObject } from '@/entities/conversations';
import { getAttrValueByPath, renderConversationAttrValue } from '@/entities/conversations/model/utils';
import { isEmptyAttrLeaf } from './dialog-data';

/**
 * Группа атрибутов с привязанным к ней списком атрибутов диалога.
 */
export type GroupWithAttributes = {
  /** Мета-данные группы (код, название и т.д.) */
  group: AttributeGroupListItem;
  /** Атрибуты диалога, входящие в эту группу */
  attributes: ConversationAttributeItem[];
};

function hasRenderedValue(
  attr: ConversationAttributeItem,
  conversationAttrValues: ConversationAttrValueObject,
): boolean {
  const attrValue = getAttrValueByPath(conversationAttrValues, attr.path);
  // Лист без значения показан строкой с прочерком, поэтому группу он не скрывает
  if (attrValue && isEmptyAttrLeaf(attrValue)) return true;
  // Формат интервалов времени здесь не нужен: проверяется только непустота значения
  const rendered = renderConversationAttrValue(attrValue, attr);

  if (rendered === null || rendered === undefined) return false;
  if (typeof rendered === 'string' && rendered.trim() === '') return false;
  if (Array.isArray(rendered) && rendered.length === 0) return false;

  return true;
}

/**
 * Строит список `GroupWithAttributes` из отсортированных групп и всех атрибутов диалога.
 * Для каждой группы выбирает атрибуты, ссылающиеся на её код через `attributeGroups`.
 * Группы без атрибутов отфильтровываются.
 */
export function buildGroupsWithAttributes(
  sortedGroups: AttributeGroupListItem[],
  allAttributes: ConversationAttributeItem[],
): GroupWithAttributes[] {
  return sortedGroups
    .map((group) => {
      const groupAttributes = allAttributes.filter((attr) =>
        attr.attributeGroups?.some((attrGroup) => group.code === attrGroup.code),
      );
      return { group, attributes: groupAttributes };
    })
    .filter((item) => item.attributes.length > 0);
}

/**
 * Фильтрует список `GroupWithAttributes`, оставляя только группы, у которых
 * хотя бы один атрибут показан в `conversationAttrValues` — значением или листом без значения.
 * Если `conversationAttrValues` не передан — возвращает пустой массив.
 */
export function buildGroupsWithValues(
  groupsWithAttributes: GroupWithAttributes[],
  conversationAttrValues: ConversationAttrValueObject | undefined,
): GroupWithAttributes[] {
  if (!conversationAttrValues) return [];

  return groupsWithAttributes.filter(({ attributes }) =>
    attributes.some((attr) => hasRenderedValue(attr, conversationAttrValues)),
  );
}
